<script lang="ts">
	import part1 from "@assets/images/zahnuto-ohniste-galerie-01.jpg?webp&h=426&imagetools";
	import part2 from "@assets/images/zahnuto-ohniste-galerie-02.jpg?webp&h=426&imagetools";
	import part3 from "@assets/images/zahnuto-ohniste-galerie-03.jpg?webp&h=426&imagetools";
	import part4 from "@assets/images/zahnuto-ohniste-galerie-04.jpg?webp&h=426&imagetools";
	import part5 from "@assets/images/zahnuto-ohniste-galerie-05.jpg?webp&h=426&imagetools";
	import part6 from "@assets/images/zahnuto-ohniste-galerie-06.jpg?webp&h=426&imagetools";
	import part0 from "@assets/images/zazehnuto-ohniste.png?webp&h=960&imagetools";
	import BuyCampfireLink from "@components/BuyCampfireLink.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import type { ClassValue } from "svelte/elements";
	import CarouselWithArrows from "./CarouselWithArrows.svelte";
	import Media from "./common/Media.svelte";

	type Side = "left" | "right" | "top" | "bottom";
	type Start = "start" | "center" | "end";
	type Align = `${Side}-${Start}`;

	interface Part {
		title: string;
		subtitle?: string;
		description: string;
		position: [number, number];
		circlePosition: [number, number];
		align: Align;
		class?: ClassValue;
	}

	const parts = [
		[
			{
				title: "Pláty na opékání (k zakoupení)",
				subtitle: "Potravinářská nerezová ocel",
				description:
					"Ideální na špekáčky, zeleninu i malý kotlík. Materiál je bezpečný pro kontakt s jídlem, nerezaví a snadno se udržuje.",
				position: [32.77, 28.33],
				circlePosition: [39.44, 35],
				align: "top-start",
			},
			{
				title: "Úchyty z obou stran",
				description:
					"Přemístění ohniště? Žádný problém. Díky praktickým úchytům ho snadno přenesete, kam potřebujete. Chce to jen dvě ruce a vizi dokonalého večera.",
				position: [65.45, 21.25],
				circlePosition: [71.11, 31.875],
				align: "right-start",
			},
			{
				title: "Tělo ohniště",
				subtitle: "Žáruvzdorná kotlářská ocel",
				position: [29.5833, 48.54166],
				align: "left-center",
				circlePosition: [47.6388, 47.92],
				description:
					"Pevný základ, který odolá vysokým teplotám i častému používání. Stejný materiál se používá na výrobu kotlů, protože když má něco vydržet 30 let, nesmí to být tenký plíšek.",
			},
		],
		[
			{
				title: "Podložka",
				subtitle: "Pozinkovaný plech",
				description:
					"Funguje jako ochranný štít pod ohništěm. Zachytí popel, nepustí teplo do země a zvládne i drsnější zacházení. Chrání trávu, dřevo i kámen před žárem a snižuje riziko, že se oheň dostane tam, kam nemá.",
				position: [32.56944, 70.833],
				circlePosition: [41.66, 71.66],
				align: "bottom-end",
				class: "!w-125.5",
			},
			{
				title: "Obal z pozinkovaného plechu",
				description:
					"Když ohniště vychladne, jednoduše ho složíte do pevného plechového obalu. Ten ho chrání při převozu a zároveň zabrání tomu, aby se popel nebo nečistoty dostaly do kufru auta, úložného boxu nebo kamkoli jinam, kam nemají.",
				position: [67.6388, 59.166],
				circlePosition: [64.8, 52.916],
				align: "right-center",
			},
		],
	] satisfies Part[][];

	const inViewThreshold = 0.95;

	function align(viewSize: number): number {
		let containerWidth: string | number = getComputedStyle(document.body).getPropertyValue("--container-width");
		const containerGap: number = parseInt(getComputedStyle(document.body).getPropertyValue("--container-gap"));
		if (containerWidth === "100%") {
			containerWidth = viewSize / inViewThreshold - containerGap * 2;
		} else {
			containerWidth = parseInt(containerWidth) - containerGap * 2;
		}
		return (viewSize / inViewThreshold - containerWidth) / 2;
	}

	/* eslint-disable @typescript-eslint/no-confusing-void-expression */
</script>

{#snippet partContent(part: Part, index: number)}
	<div class="bg-nov-light-100 p-4 lg:p-6 flex flex-col gap-2 rounded-2xl">
		<div class="flex gap-2 items-center">
			<div class="bg-nov-red-300 size-5 text-xs/5 font-extrabold text-white rounded-full text-center">
				{index + 1}
			</div>
			<h2 class="text-nov-red-300 font-semibold text-md/5">
				<Formatted text={part.title} />
			</h2>
		</div>
		{#if "subtitle" in part}
			<h3 class="uppercase font-bold text-xs/4 text-nov-dark-900">
				<Formatted text={part.subtitle} />
			</h3>
		{/if}
		<p class="text-xs/4 text-nov-dark-900">
			<Formatted text={part.description} />
		</p>
	</div>
{/snippet}

<section class="grid-container full-width">
	<h2 class="text-10/12 lg:text-title1 font-bold tracking-[-0.005em] text-center mb-4">
		<Formatted text="Z čeho se ohniště skládá?" />
	</h2>
	<p class="text-5.5/7.5 lg:text-title2 mb-8 lg:mb-12 text-nov-red-300 text-center">
		<Formatted
			text="Když jsme ohniště navrhovali, přemýšleli jsme nad každým detailem. Aby se dobře přenášelo, jednoduše používalo,
		bezpečně hořelo a nezanechávalo za sebou víc, než příjemný večer."
		/>
	</p>

	<div class="flex flex-col lg:hidden gap-2 mb-6">
		{#each parts[0] ?? [] as part, index (index)}
			{@render partContent(part, index)}
		{/each}
	</div>

	<div class="full-width">
		<div class="mx-auto max-w-screen-2xl relative">
			<Media
				source={{ src: part0, type: "image/webp" }}
				class="2xl:rounded-lg"
				alt="Ohniště"
				width={1440}
				height={960}
			/>

			<div class="absolute inset-0">
				{#each parts.flat() as part, index (index)}
					<div
						class="absolute rounded-full p-1.5 lg:p-2.5 bg-white -translate-x-1/2 -translate-y-1/2"
						style="left: {part.circlePosition[0]}%; top: {part.circlePosition[1]}%"
					>
						<div class="bg-nov-red-300 size-5 text-xs/5 font-extrabold text-white rounded-full text-center">
							{index + 1}
						</div>
					</div>
					<div class="absolute p-2" style="left: {part.position[0]}%; top: {part.position[1]}%">
						<div class="size-6"></div>
						<div
							class={[
								"absolute w-60 xl:w-82.5 hidden lg:block",
								"class" in part ? part.class : "",
								{
									"left-start": "right-full mr-2 xl:mr-6 bottom-0",
									"left-center": "right-full mr-2 xl:mr-6 top-1/2 -translate-y-1/2",
									"left-end": "right-full mr-2 xl:mr-6 top-0",
									"top-start": "bottom-full mb-2 xl:mb-6 right-0",
									"top-center": "bottom-full mb-2 xl:mb-6 left-1/2 -translate-x-1/2",
									"top-end": "bottom-full mb-2 xl:mb-6 left-0",
									"right-start": "left-full ml-2 xl:ml-6 bottom-0",
									"right-center": "left-full ml-2 xl:ml-6 top-1/2 -translate-y-1/2",
									"right-end": "left-full ml-2 xl:ml-6 top-0",
									"bottom-start": "top-full mt-2 xl:mt-6 right-0",
									"bottom-center": "top-full mt-2 xl:mt-6 left-1/2 -translate-x-1/2",
									"bottom-end": "top-full mt-2 xl:mt-6 left-0",
								}[part.align],
							]}
						>
							{@render partContent(part, index)}
						</div>
					</div>
				{/each}
			</div>
		</div>
	</div>

	<div class="flex flex-col lg:hidden gap-2 mt-6 mb-8">
		{#each parts[1] ?? [] as part, index (index)}
			{@render partContent(part, (parts[0]?.length ?? 0) + index)}
		{/each}
	</div>

	<CarouselWithArrows
		wrapperClass="full-width lg:mt-2"
		class="h-82 lg:h-107"
		items={[part1, part2, part3, part4, part5, part6]}
		options={{ loop: true, align, inViewThreshold }}
	>
		{#snippet item(img: string)}
			<Media
				width={0}
				height={426}
				source={{ src: img, type: "image/webp" }}
				alt="Fotografie ohniště"
				wrapperClass="aspect-square h-full mx-[0.1875rem]"
				class="h-full object-cover w-full rounded-lg"
			/>
		{/snippet}
	</CarouselWithArrows>

	<BuyCampfireLink class="mt-6 mb-24 mx-auto" />
</section>
